import React from "react";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import AutorizacionDetail from "./AutorizacionDetail";
import Badge from "../../../../../../components/common/PL/Badge/Badge";
import Button from "../../../../../../components/common/PL/Button/Button";
import SolicitarAutorizacionModal, {
  AuthorizingCompany,
  SolicitarAutorizacionSubmitData,
} from "./SolicitarAutorizacionModal";
import { AuthorizationGroup } from "./transportAuthorizationTypes";
import { useElapsedSeconds } from "./useElapsedSeconds";

const REFRESH_AVAILABLE_AFTER_SECONDS = 10;

type AutorizacionesListProps = {
  groups: AuthorizationGroup[];
  isLoading: boolean;
  /** Qué tarjeta está desplegada. Puramente visual — no tiene nada que ver con qué autorización
   * está tildada (`selectedRowId`), así que ocultar una tarjeta nunca la destilda. */
  expandedGroupId: string | null;
  onToggleExpandGroup: (group: AuthorizationGroup) => void;
  selectedRowId: string | null;
  onSelectRow: (groupId: string, rowId: string) => void;
  onResendRow: (rowId: string) => void;
  onRegistrarPorOtroMedio: (rowId: string) => void;
  onRequestAuthorization: (data: SolicitarAutorizacionSubmitData) => Promise<boolean>;
  onRefresh: () => void;
  disabled?: boolean;
  prefillVehiclePlate?: string;
  prefillRemito?: string;
  isSolicitarModalOpen: boolean;
  onOpenSolicitarModal: () => void;
  onCloseSolicitarModal: () => void;
  solicitarModalPrefillCompany?: AuthorizingCompany;
};

const AutorizacionesList: React.FC<AutorizacionesListProps> = ({
  groups,
  isLoading,
  expandedGroupId,
  onToggleExpandGroup,
  selectedRowId,
  onSelectRow,
  onResendRow,
  onRegistrarPorOtroMedio,
  onRequestAuthorization,
  onRefresh,
  disabled = false,
  prefillVehiclePlate,
  prefillRemito,
  isSolicitarModalOpen,
  onOpenSolicitarModal,
  onCloseSolicitarModal,
  solicitarModalPrefillCompany,
}) => {
  const [lastRefreshAt, setLastRefreshAt] = React.useState<number | null>(null);
  const elapsedSinceRefresh = useElapsedSeconds(lastRefreshAt ?? 0);
  const canRefresh =
    lastRefreshAt === null || elapsedSinceRefresh >= REFRESH_AVAILABLE_AFTER_SECONDS;

  const handleRefreshClick = () => {
    if (!canRefresh) return;
    setLastRefreshAt(Date.now());
    onRefresh();
  };

  return (
    <div className="transportEmpresaSection">
      <div className="transportAuthHeader">
        <div className="transportAuthHeaderLeft">
          <p>Autorizaciones</p>
          <span className="transportAuthBadge">{groups.length}</span>
          <Button
            variant="outline"
            size="small"
            onClick={handleRefreshClick}
            disabled={!canRefresh}
            loading={isLoading}
            className="transportAuthRefreshButton"
          >
            <RefreshIcon fontSize="small" />
          </Button>
        </div>
        <ExpandLessIcon style={{ color: "#64748B", cursor: "pointer" }} />
      </div>

      <div className="transportAuthList">
        {isLoading && <div className="transportAuthEmptyState">Buscando autorizaciones…</div>}

        {!isLoading && groups.length === 0 && (
          <div className="transportAuthEmptyState">
            No hay autorizaciones de acceso para esta empresa transportista.
          </div>
        )}

        {groups.map((group) => {
          const isExpanded = expandedGroupId === group.id;
          // La tarjeta se resalta si tiene la autorización tildada, sin importar si está
          // desplegada o no — ocultarla no le hace perder la marca.
          const hasSelectedRow = group.rows.some((row) => row.id === selectedRowId);

          return (
            <div
              key={group.id}
              className={`transportAuthCard ${hasSelectedRow ? "transportAuthCardSelected" : ""}`}
              onClick={() => !disabled && onToggleExpandGroup(group)}
            >
              <div
                className={`transportAuthCardHeaderArea ${
                  isExpanded ? "transportAuthCardHeaderAreaSelected" : ""
                }`}
              >
                <div className="transportAuthCardTop">
                  <div className="transportAuthCardTitleGroup">
                    <div className="transportAuthTitleBlock">
                      <h4 className="transportAuthCardTitle">{group.title}</h4>
                      {group.subtitle && (
                        <span className="transportAuthCardSubtitle">{group.subtitle}</span>
                      )}
                    </div>
                    <div className="transportPillRow">
                      {group.operationLabels.map((label) => (
                        <Badge
                          key={label}
                          variant={label === "Descarga" ? "yellow" : "blue"}
                          label={label}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="transportAuthCardRight">
                    <span className="transportLugaresDispCapsule">
                      {group.availableSlots}{" "}
                      {group.availableSlots === 1 ? "lugar disp." : "lugares disp."}
                    </span>
                    <ExpandMoreIcon
                      style={{
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        color: "#64748B",
                      }}
                    />
                  </div>
                </div>

                <div className="transportAuthCardBottom">
                  <LocalShippingOutlinedIcon fontSize="small" style={{ color: "#94A3B8" }} />
                  <span className="transportAuthCardMetaText">
                    {group.usedTrucks}/{group.totalTrucks} camiones &nbsp;|&nbsp;{" "}
                    {group.authorizerCount} autorizadores
                    {group.dock !== undefined && <>&nbsp;|&nbsp;Número de dock: {group.dock}</>}
                  </span>
                </div>
              </div>

              <div className={`transportAuthCardAccordionContent ${isExpanded ? "open" : ""}`}>
                <div className="transportAuthCardAccordionInner">
                  <AutorizacionDetail
                    group={group}
                    selectedRowId={selectedRowId}
                    onSelectRow={(rowId) => onSelectRow(group.id, rowId)}
                    onResendRow={onResendRow}
                    onRegistrarPorOtroMedio={onRegistrarPorOtroMedio}
                    disabled={disabled}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        variant="gradient"
        onClick={onOpenSolicitarModal}
        className="transportSolicitarAutorizacionButton"
        disabled={disabled}
      >
        Solicitar autorizacion <AddIcon fontSize="small" style={{ marginLeft: 8 }} />
      </Button>

      <SolicitarAutorizacionModal
        open={isSolicitarModalOpen}
        onClose={onCloseSolicitarModal}
        onSubmit={onRequestAuthorization}
        prefillVehiclePlate={prefillVehiclePlate}
        prefillRemito={prefillRemito}
        prefillCompany={solicitarModalPrefillCompany}
      />
    </div>
  );
};

export default AutorizacionesList;
